import React, { useState, useEffect } from 'react';
import { resolveImageUrl } from '@/utils/storage';
import { twMerge } from 'tailwind-merge';

interface ImageWithLoaderProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt?: string;
    className?: string;
    placeholderClassName?: string;
    fallback?: React.ReactNode;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
    src,
    alt = '',
    className = '',
    placeholderClassName = '',
    fallback = null,
    ...props
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState<string>('');

    useEffect(() => {
        // Reset state when src changes
        if (!src) {
            setIsLoading(false);
            setHasError(true);
            return;
        }

        setIsLoading(true);
        setHasError(false);
        const resolvedSrc = resolveImageUrl(src);
        setCurrentSrc(resolvedSrc || '');

        // Preload image
        const img = new Image();
        img.src = resolvedSrc || '';
        img.onload = () => setIsLoading(false);
        img.onerror = () => {
            setIsLoading(false);
            setHasError(true);
        };

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [src]);

    if (!src) return null;

    return (
        <div className={twMerge("relative overflow-hidden", className)}>
            {/* Loading Placeholder */}
            {isLoading && (
                <div className={`absolute inset-0 bg-slate-200 dark:bg-slate-700 animate-pulse ${placeholderClassName}`} />
            )}

            {/* Error Placeholder */}
            {hasError && !isLoading && (
                fallback ? fallback : (
                    <div className={`absolute inset-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 ${placeholderClassName}`}>
                        <span className="text-xs">!</span>
                    </div>
                )
            )}

            {/* Actual Image */}
            {!hasError && (
                <img
                    src={currentSrc}
                    alt={alt}
                    className={twMerge(
                        "w-full h-full object-cover transition-opacity duration-300",
                        isLoading ? "opacity-0" : "opacity-100"
                    )}
                    style={{ objectFit: 'cover' }}
                    {...props}
                />
            )}
        </div>
    );
};

export default ImageWithLoader;
